import { useQuery } from '@apollo/client'
import { Box, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { isAddress } from 'viem'

import { CONTRACT_STUDIO_TEMPLATE_ADDRESS } from '@/constants/blockchain'
import { useAdminsContext } from '@/contexts/AdminsContext'
import { useAuth } from '@/plugins/auth'
import {
  GetCollectionById,
  QUERY_GET_COLLECTION_BY_ID,
  QUERY_GET_COLLECTION_ITEMS,
  QUERY_GET_SUGGESTED_COLLECTION_ITEMS,
} from '@/schemas/collections.schema'
import { BodyStack } from '@/styled/components'
import { shortenHash } from '@/utils/utils'

import RootButton from '../Buttons/RootButton'
import ContentFlagged from '../ContentFlagged'
import ExploreTab from '../Explore/ExploreTab'
import Loading from '../Loading'
import FlagContentModal from '../Modals/Content/FlagContentModal'
import ModalSkeleton from '../Modals/ModalSkeleton'
import NotFound from '../NotFound'
import classes from './CollectionPage.module.css'

const Component: React.FC = () => {
  const { id: idParams } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const [opened, { open, close }] = useDisclosure(false)

  const { address } = useAuth()
  const { admins } = useAdminsContext()

  const isAdmin = useMemo(
    () => admins.map(a => a.toLowerCase()).includes(address?.toLowerCase() || ''),
    [address, admins]
  )

  // Get collection by id
  const { loading, data, refetch } = useQuery<GetCollectionById>(QUERY_GET_COLLECTION_BY_ID, {
    variables: { id: idParams?.toLowerCase() },
  })

  // Is Collection flagged?
  const flagged = useMemo(() => data?.collection?.flagged || false, [data])

  // Collection name from indexer
  const collectionNameFetched = useMemo(() => data?.collection?.name || '', [data])

  // Total of items
  const totalOfItems = useMemo(() => data?.collection?.total.agg.count || 0, [data])

  // Collection name
  const collectionName = useMemo(() => {
    // Studio artwork hardcoded
    if (idParams?.toLowerCase() === CONTRACT_STUDIO_TEMPLATE_ADDRESS.toLowerCase()) {
      return 'Artwork Template'
    }
    // Return fetched collection name if available
    if (collectionNameFetched !== '') {
      return collectionNameFetched
    }
    // Fallback to collectionId (address)
    if (isAddress(idParams || '')) {
      return isMobile ? shortenHash(idParams || '') : idParams || ''
    }
    return ''
  }, [idParams, collectionNameFetched])

  const isArtwork = useMemo(() => {
    // Studio artwork hardcoded
    if (idParams?.toLowerCase() === CONTRACT_STUDIO_TEMPLATE_ADDRESS.toLowerCase()) {
      return true
    }
    return false
  }, [idParams])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleClose = useCallback(() => {
    close()
    refetch()
  }, [close, refetch])

  return (
    <>
      {loading && <Loading />}
      {!loading && !data?.collection && <NotFound />}
      {!loading && flagged && !isAdmin && <ContentFlagged contentType="Collection" />}
      {!loading && (!flagged || isAdmin) && data?.collection && (
        <BodyStack style={{ gap: '30px', minHeight: '100vh' }}>
          <Stack
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
            }}
          >
            <ModalSkeleton
              opened={opened}
              onClose={handleClose}
              title={t('common.unflag', 'Unflag')}
            >
              <FlagContentModal
                close={handleClose}
                contentType="collection_items"
                contentId={idParams?.toLowerCase() || ''}
                isFlagged={flagged}
              />
            </ModalSkeleton>
            <Title order={isMobile ? 3 : 2}>
              {t('components.items.collectionTitle', 'Collection')}
            </Title>
            {isArtwork && (
              <RootButton secondary onClick={() => navigate('/studio/templates/1-1')}>
                {t('components.profile.createItem', 'Create Item')}
              </RootButton>
            )}
            {isAdmin && (
              <Stack style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                {flagged ? (
                  <>
                    <Box className={classes.flaggedBox}>
                      <Text className={classes.flaggedText}>
                        {t('common.collectionFlagged', 'This collection is flagged.')}
                      </Text>
                    </Box>
                    <RootButton style1={false} error onClick={open}>
                      {t('common.unflag', 'Unflag')}
                    </RootButton>
                  </>
                ) : (
                  <RootButton style1={false} error onClick={open}>
                    {t('common.flag', 'Flag')}
                  </RootButton>
                )}
              </Stack>
            )}
          </Stack>
          <ExploreTab
            id="collections"
            title={collectionName}
            // Specific
            total={totalOfItems}
            contentType="collection_items"
            filterOptions={isAdmin ? ['All', 'Flagged'] : []}
            sortOptions={['# transfers', 'publish date']}
            query={QUERY_GET_COLLECTION_ITEMS}
            queryId={idParams?.toLowerCase()}
            suggestionQuery={QUERY_GET_SUGGESTED_COLLECTION_ITEMS}
            searchEnabled={false}
            triggerRefetch={refetch}
            isAdmin={isAdmin}
          />
        </BodyStack>
      )}
    </>
  )
}

Component.displayName = 'CollectionPage'

// eslint-disable-next-line import/prefer-default-export
export { Component }
